import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import LocalLink from "@/components/ui/LocalLink";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, Sun, Moon } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/ui/LanguageSwitcher";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [theme, setTheme] = useState<'dark'|'light'>('dark');
  const { t, i18n } = useTranslation();

  const getPath = (path: string) => {
    if (i18n.language === 'en' || !i18n.language) return path;
    return `/${i18n.language}${path}`;
  };

  const navLinks = [
    { name: "SIDEMEN", href: getPath("/") },
    { 
      name: t("menu.about", "About Us"), 
      href: "#",
      submenu: [
        { name: t("menu.about", "About Us"), href: getPath("/about-us") },
        { name: t("menu.facilities", "Facilities"), href: getPath("/facilities") }
      ]
    },
    { 
      name: t("menu.villas", "Villas & Rates"), 
      href: "#",
      submenu: [
        { name: t("menu.allRooms", "All Rooms"), href: getPath("/accommodation") },
        { name: t("rooms.deluxe-bungalow-mountain-view.title", "Deluxe Bungalow"), href: getPath("/accommodation/deluxe-bungalow-mountain-view") },
        { name: t("rooms.suite-room-mountain-view.title", "Suite Room"), href: getPath("/accommodation/suite-room-mountain-view") },
        { name: t("rooms.private-pool-villa-mountain-view.title", "Private Pool Villa"), href: getPath("/accommodation/private-pool-villa-mountain-view") }
      ]
    },
    { name: t("menu.experiences", "Things to Do"), href: getPath("/experiences") },
    { name: t("menu.gallery", "Gallery"), href: getPath("/gallery") },
    { name: t("menu.news", "News"), href: getPath("/news") },
    { name: t("menu.contact", "Contact"), href: getPath("/contact") },
  ];


  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    if (newTheme === 'light') {
      document.documentElement.classList.add('light');
    } else {
      document.documentElement.classList.remove('light');
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-black/90 backdrop-blur-md" : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-6 h-24 flex items-center justify-between border-b border-white/5 relative z-50">
        <LocalLink to="/" className="flex items-center">
          <img 
            src="/logo.webp" 
            alt="Patal Kikian Villas" 
            width="160"
            height="48"
            className="h-10 md:h-12 w-auto object-contain"
          />
        </LocalLink>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex gap-8 items-center">
          {navLinks.map((link) => (
            <div 
              key={link.name} 
              className="relative group"
              onMouseEnter={() => link.submenu && setActiveDropdown(link.name)}
              onMouseLeave={() => link.submenu && setActiveDropdown(null)}
            >
              {link.submenu ? (
                <button
                  className="text-xs uppercase tracking-widest text-white/80 hover:text-white transition-colors flex items-center gap-1 py-4 cursor-pointer bg-transparent border-0"
                >
                  {link.name}
                  <ChevronDown size={14} className="opacity-70" />
                </button>
              ) : (
                <LocalLink
                  to={link.href}
                  className="text-xs uppercase tracking-widest text-white/80 hover:text-white transition-colors flex items-center gap-1 py-4"
                >
                  {link.name}
                </LocalLink>
              )}
              
              {/* Dropdown */}
              {link.submenu && (
                <AnimatePresence>
                  {activeDropdown === link.name && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full left-0 mt-[-5px] bg-black/95 backdrop-blur-md border border-white/10 p-4 min-w-[200px]"
                    >
                      {link.submenu.map(subItem => (
                        <LocalLink
                          key={subItem.name}
                          to={subItem.href}
                          className="block text-xs uppercase tracking-widest text-white/70 hover:text-gold py-2 transition-colors"
                        >
                          {subItem.name}
                        </LocalLink>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </div>
          ))}
          <div className="flex items-center gap-4 ml-4">
            <LanguageSwitcher />
            <button onClick={toggleTheme} className="text-white hover:text-gold transition-colors cursor-pointer" aria-label="Toggle Theme">
              {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
            </button>
          </div>
          <a
            href="https://wa.me/6281353278504"
            target="_blank"
            rel="noopener noreferrer"
            className="ml-4 px-6 py-3 bg-gold text-black hover:bg-white transition-colors uppercase tracking-widest text-xs font-semibold"
          >
            {t("cta.book", "Reserve Now")}
          </a>
        </nav>

        {/* Mobile Controls */}
        <div className="lg:hidden flex items-center gap-4">
          <LanguageSwitcher />
          <button onClick={toggleTheme} className="text-white cursor-pointer" aria-label="Toggle Theme">
            {theme === 'dark' ? <Sun size={24} /> : <Moon size={24} />}
          </button>
          <button
            className="text-white cursor-pointer"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle Menu"
          >
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.4, ease: "easeInOut" }}
            className="lg:hidden fixed inset-0 z-40 bg-black/98 backdrop-blur-xl pt-24 sm:pt-32 px-6 pb-16 overflow-y-auto h-[100dvh] block"
          >
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <div key={link.name} className="flex flex-col">
                  {link.submenu ? (
                    <button
                      className="text-base sm:text-lg uppercase tracking-widest text-white hover:text-gold transition-colors py-2.5 sm:py-3.5 border-b border-white/5 text-left flex items-center justify-between w-full bg-transparent cursor-pointer"
                      onClick={() => setActiveDropdown(activeDropdown === link.name ? null : link.name)}
                    >
                      {link.name}
                      <ChevronDown size={18} className={`transition-transform ${activeDropdown === link.name ? "rotate-180 text-gold" : ""}`} />
                    </button>
                  ) : (
                    <LocalLink
                      to={link.href}
                      className="text-base sm:text-lg uppercase tracking-widest text-white hover:text-gold transition-colors py-2.5 sm:py-3.5 border-b border-white/5"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.name}
                    </LocalLink>
                  )}
                  
                  <AnimatePresence>
                    {link.submenu && activeDropdown === link.name && (
                      <motion.div 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="flex flex-col pl-6 py-2 overflow-hidden"
                      >
                        {link.submenu.map(subItem => (
                          <LocalLink
                            key={subItem.name}
                            to={subItem.href}
                            className="text-xs sm:text-sm uppercase tracking-widest text-white/70 hover:text-gold py-2 sm:py-2.5 transition-colors"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            - {subItem.name}
                          </LocalLink>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
            
            <div className="mt-6 pb-12 flex flex-col gap-4">
              
              <a
                href="https://wa.me/6281353278504"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-center px-6 py-3.5 sm:py-4.5 bg-gold text-black hover:bg-white transition-colors uppercase tracking-widest text-base sm:text-lg font-semibold"
              >
                Reserve Now
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
